import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, Search } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const PAGE_LABELS: Record<string, string> = {
  home: "Inicio",
  about: "Sobre Nosotros",
  services: "Servicios",
  fleet: "Flota",
  contact: "Contacto",
  transfers: "Transfers",
  tours: "Tours",
  destinations: "Destinos",
  privacy: "Política de Privacidad",
  terms: "Términos y Condiciones",
  cookies: "Política de Cookies",
};

interface PageSeo {
  id: string;
  page_key: string;
  meta_title: string | null;
  meta_description: string | null;
  meta_keywords: string | null;
  og_image_url: string | null;
  canonical_url: string | null;
}

export function SeoSection() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<PageSeo | null>(null);
  const [form, setForm] = useState<Partial<PageSeo>>({});

  const { data: pages = [], isLoading } = useQuery({
    queryKey: ["admin_page_seo"],
    queryFn: async () => {
      const { data, error } = await supabase.from("page_seo").select("*").order("created_at");
      if (error) throw error;
      return data as PageSeo[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (values: Partial<PageSeo> & { id: string }) => {
      const { id, ...rest } = values;
      const { error } = await supabase.from("page_seo").update(rest).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_page_seo"] });
      queryClient.invalidateQueries({ queryKey: ["page_seo"] });
      toast({ title: "SEO actualizado" });
      setEditing(null);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const openEdit = (page: PageSeo) => {
    setEditing(page);
    setForm({
      meta_title: page.meta_title ?? "",
      meta_description: page.meta_description ?? "",
      meta_keywords: page.meta_keywords ?? "",
      og_image_url: page.og_image_url ?? "",
      canonical_url: page.canonical_url ?? "",
    });
  };

  const handleSave = () => {
    if (!editing) return;
    updateMutation.mutate({ id: editing.id, ...form });
  };

  if (isLoading) return <p className="text-muted-foreground">Cargando…</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Search className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-semibold">SEO por Página</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        Configura meta título, descripción, keywords e imagen OG para cada página del sitio.
      </p>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Página</TableHead>
              <TableHead>Meta Título</TableHead>
              <TableHead>Meta Descripción</TableHead>
              <TableHead className="w-20">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pages.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{PAGE_LABELS[p.page_key] ?? p.page_key}</TableCell>
                <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                  {p.meta_title || "—"}
                </TableCell>
                <TableCell className="max-w-[300px] truncate text-sm text-muted-foreground">
                  {p.meta_description || "—"}
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(p)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              SEO — {editing ? (PAGE_LABELS[editing.page_key] ?? editing.page_key) : ""}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between">
                <Label>Meta Título</Label>
                <span className="text-xs text-muted-foreground">{(form.meta_title?.length ?? 0)}/60</span>
              </div>
              <Input
                value={form.meta_title ?? ""}
                onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
                maxLength={60}
                placeholder="Título de la página para Google"
              />
            </div>

            <div>
              <div className="flex justify-between">
                <Label>Meta Descripción</Label>
                <span className="text-xs text-muted-foreground">{(form.meta_description?.length ?? 0)}/160</span>
              </div>
              <Textarea
                value={form.meta_description ?? ""}
                onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                maxLength={160}
                placeholder="Descripción que aparece en los resultados de búsqueda"
                rows={3}
              />
            </div>

            <div>
              <Label>Meta Keywords</Label>
              <Input
                value={form.meta_keywords ?? ""}
                onChange={(e) => setForm({ ...form, meta_keywords: e.target.value })}
                placeholder="keyword1, keyword2, keyword3"
              />
            </div>

            <div>
              <Label>OG Image URL</Label>
              <Input
                value={form.og_image_url ?? ""}
                onChange={(e) => setForm({ ...form, og_image_url: e.target.value })}
                placeholder="https://..."
              />
              {form.og_image_url && (
                <img src={form.og_image_url} alt="OG preview" className="mt-2 h-24 rounded border object-cover" />
              )}
            </div>

            <div>
              <Label>Canonical URL</Label>
              <Input
                value={form.canonical_url ?? ""}
                onChange={(e) => setForm({ ...form, canonical_url: e.target.value })}
                placeholder="https://tusitio.com/pagina"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
